import { createFileRoute, useParams } from '@tanstack/react-router';
import { useCallback, useEffect, useState, type JSX } from 'react';

import { CtLogo } from '@/components/brand/index.js';
import { optionalSessionJwt, signInWithSso } from '@/lib/sso.js';
import { logger } from '@/logger.js';

export const Route = createFileRoute('/share/$shareId')({
  component: SharedConversationPage,
});

type ViewerState =
  | { kind: 'loading' }
  | { kind: 'ready'; html: string }
  | { kind: 'needs-auth' }
  | { kind: 'not-found' }
  | { kind: 'error'; message: string };

/**
 * Fetch the shared snapshot HTML from the public serving endpoint.
 *
 * Attaches `Authorization: Bearer <jwt>` ONLY when a platform JWT already
 * exists in the identity store — renewed silently when it has aged out, never
 * via the interactive login path, so an external viewer with no session simply
 * sends no bearer.
 * A `public` share serves without auth; an `internal` share 401s without a
 * valid bearer (the sign-in CTA); a missing/revoked/expired share 404s.
 *
 * This is the one call that deliberately stays off `lib/api-fetch.ts`: the
 * wrapper renews the platform JWT through Entra and retries a 401, which for a
 * viewer who legitimately has no session means an SSO round-trip nobody asked
 * for. Here a 401 is a valid answer ("sign in to see this"), not a failure to
 * repair, so there is no retry; `optionalSessionJwt` renews only a session
 * that already exists.
 */
async function fetchSnapshot(shareId: string): Promise<ViewerState> {
  const jwt = await optionalSessionJwt();
  const headers: Record<string, string> = {};
  if (jwt !== null && jwt.length > 0) {
    headers['Authorization'] = `Bearer ${jwt}`;
  }
  const res = await fetch(`/v1/shares/${encodeURIComponent(shareId)}`, {
    method: 'GET',
    headers,
    // Never serve a snapshot from a client/intermediary cache — a revoked or expired link must
    // fail immediately. Auth is header-only, so no cookies are needed.
    cache: 'no-store',
    credentials: 'omit',
  });
  if (res.status === 401) return { kind: 'needs-auth' };
  if (res.status === 404) return { kind: 'not-found' };
  if (!res.ok)
    return {
      kind: 'error',
      message: `Failed to load snapshot (${res.status})`,
    };
  const html = await res.text();
  return { kind: 'ready', html };
}

/**
 * Chrome-free public viewer for a shared conversation snapshot.
 *
 * Deliberately isolated from the authenticated app: it does not touch the
 * Teams SDK and never triggers `checkAccessDenied`. The returned HTML is
 * rendered inside a fully sandboxed iframe (empty `sandbox` = no scripts, no
 * same-origin, no forms) so a snapshot can never run code or reach the parent
 * origin. The AuthGate/TeamsOnlyGate/MaintenanceGate walls are bypassed for
 * `/share/*` in boot.tsx so this loads for an unauthenticated viewer.
 */
function SharedConversationPage(): JSX.Element {
  const { shareId } = useParams({ from: '/share/$shareId' });
  const [state, setState] = useState<ViewerState>({ kind: 'loading' });

  const load = useCallback(async (): Promise<void> => {
    setState({ kind: 'loading' });
    try {
      setState(await fetchSnapshot(shareId));
    } catch (err: unknown) {
      logger.warn('Shared snapshot fetch failed', err);
      setState({
        kind: 'error',
        message: 'Something went wrong loading this snapshot.',
      });
    }
  }, [shareId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.kind === 'loading') {
    return (
      <ViewerShell>
        <p className="text-sm text-muted-foreground" role="status">
          Loading snapshot…
        </p>
      </ViewerShell>
    );
  }

  if (state.kind === 'needs-auth') {
    return <SignInScreen onSignedIn={() => void load()} />;
  }

  if (state.kind === 'not-found') {
    return (
      <ViewerShell>
        <p className="text-sm text-muted-foreground">
          This link is unavailable or has been revoked.
        </p>
      </ViewerShell>
    );
  }

  if (state.kind === 'error') {
    return (
      <ViewerShell>
        <p className="text-sm text-destructive">{state.message}</p>
      </ViewerShell>
    );
  }

  return (
    <div
      className="flex h-full min-h-0 w-full flex-col"
      style={{ height: 'var(--app-height, 100dvh)' }}
    >
      <header className="flex shrink-0 items-center gap-3 border-b border-border/40 bg-background px-4 py-3">
        <CtLogo
          variant="mark"
          tone="mono"
          className="size-6 text-[#18399b] [[data-theme='dark']_&]:text-white"
          aria-label="Customertimes"
        />
        <span className="text-sm font-medium text-foreground">
          Read-only snapshot
        </span>
      </header>
      <iframe
        srcDoc={state.html}
        sandbox=""
        referrerPolicy="no-referrer"
        title="Shared conversation"
        className="min-h-0 w-full flex-1 border-0 bg-white"
      />
    </div>
  );
}

/** Shared full-screen frame for the loading / not-found / error states. */
function ViewerShell({ children }: { children: JSX.Element }): JSX.Element {
  return (
    <div
      className="flex w-full flex-col items-center justify-center gap-6 bg-background px-6 text-center"
      style={{ height: 'var(--app-height, 100dvh)' }}
    >
      <div className="flex items-center gap-3">
        <CtLogo
          variant="mark"
          tone="mono"
          className="size-9 text-[#18399b] [[data-theme='dark']_&]:text-white"
          aria-label="Customertimes"
        />
        <span className="font-heading text-xl font-bold tracking-tight text-foreground">
          Agent
        </span>
      </div>
      {children}
    </div>
  );
}

/** 401 screen — this snapshot is internal, so a Customertimes sign-in is required. */
function SignInScreen({ onSignedIn }: { onSignedIn: () => void }): JSX.Element {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSignIn(): Promise<void> {
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      await signInWithSso();
      onSignedIn();
    } catch {
      setError('Sign-in failed. Try again.');
      setLoading(false);
    }
  }

  return (
    <ViewerShell>
      <div className="flex flex-col items-center gap-4">
        <p className="max-w-sm text-sm text-muted-foreground">
          This snapshot is restricted to Customertimes employees.
        </p>
        <button
          type="button"
          onClick={() => void onSignIn()}
          disabled={loading}
          className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-primary px-6 text-sm font-medium text-primary-foreground transition-all hover:shadow-lg active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loading ? 'Signing in…' : 'Sign in with Microsoft to view'}
        </button>
        {error !== null && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
      </div>
    </ViewerShell>
  );
}
